import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useForm, useFormState } from "react-final-form";
import classNames from "classnames";
import { isNotEmpty } from "utils/validate";
import { findDictionaryItemByValue, recipeParameterUnitsType } from "shared/rsDictionary";
import Input from "rs-components/Input";
import SearchSelectInput from "rs-components/Input/components/SearchSelectInput";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import { FormField } from "../../index";
import { ResultElementParameterType, } from "store/rs-workspace/rs-workspace.types";
import { parameterTypesSelector } from "store/global/global.selectors";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { workspaceRecipesSelector } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { getRecipeParameterUnitsItems } from "store/rs-workspace/utils/results.utils";
import { getResultElementRecipeParameterAndAlternativeStepsItems } from "store/rs-workspace/utils/common.utils";
import * as styles from "../../styles.module.scss";
const RecipeParameterFormField = ({ allowMultipleUnits, selectedParameters, restrictions = {}, }) => {
    const form = useForm();
    const formState = useFormState();
    const parameterTypes = useSelector(parameterTypesSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const editable = useSelector(isEditableSelector);
    const { name, type, units } = useMemo(() => {
        const { type, name } = {
            ...formState?.values,
        };
        switch (type) {
            case ResultElementParameterType.RECIPE:
                return { name, type, units: (formState?.values).units };
            case ResultElementParameterType.ALTERNATIVE_FLOW:
                return { name, type, units: undefined };
        }
    }, [formState]);
    const items = useMemo(() => getResultElementRecipeParameterAndAlternativeStepsItems(recipes, selectedParameters, parameterTypes, formState?.values, allowMultipleUnits), [recipes, selectedParameters, parameterTypes, formState?.values, allowMultipleUnits]);
    const selectedItem = useMemo(() => items.find(({ value }) => (typeof value === "string" ? false : value.name === name)), [items, name]);
    const getUnitsItems = useCallback((parameterName, units) => {
        if (!parameterName || !units) {
            return [];
        }
        return getRecipeParameterUnitsItems(parameterName, units, parameterTypes, selectedParameters).filter((item) => isNotEmpty(item.value, true));
    }, [parameterTypes, selectedParameters]);
    const handleChangeAlternativeStep = useCallback((item) => {
        form.batch(() => {
            form.reset({ type: ResultElementParameterType.ALTERNATIVE_FLOW });
            form.change(FormField.NAME, item.name);
        });
    }, [form]);
    const handleChangeParameter = useCallback((item) => {
        const value = (items.find(({ value }) => {
            if (typeof value === "string" || value.isAlternativeStep) {
                return false;
            }
            const { name, unitsCategory } = value;
            return name === item.name && unitsCategory === item.unitsCategory;
        })?.value || {});
        const units = value.selectedUnits[0];
        const availableUnits = getUnitsItems(item.name, units).map(({ value }) => value);
        const unitsToSelect = units && availableUnits.includes(units) ? units : availableUnits[0];
        form.batch(() => {
            form.reset({ type: ResultElementParameterType.RECIPE });
            form.change(FormField.NAME, item.name);
            form.change(FormField.UNITS, findDictionaryItemByValue(recipeParameterUnitsType, unitsToSelect)?.value);
        });
    }, [items, getUnitsItems, form]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.field },
            React.createElement(Input, { readonly: !editable || (!name && !items.length), component: SearchSelectInput, disableFormChange: true, onChange: (value) => value.isAlternativeStep
                    ? handleChangeAlternativeStep(value)
                    : handleChangeParameter(value), name: FormField.NAME, label: "Parameter", value: selectedItem, undefinedReadonlyPlaceholder: !editable, placeholder: !items.length ? "No parameters to add." : "Select", items: items, classes: {
                    component: classNames({ [styles["field-noSources"]]: !items.length }),
                    label: styles["field-label"],
                }, restrictions: restrictions?.ParameterName })),
        type === ResultElementParameterType.RECIPE && isNotEmpty(name, true) && (React.createElement("div", { className: styles.field },
            React.createElement(Input, { readonly: !editable, component: SelectInput, name: FormField.UNITS, label: "Unit", items: getUnitsItems(name, units), valueFormatter: ({ value }) => value, classes: { component: styles.selectWrapper, label: styles["field-label"] }, itemRenderer: SelectItemRenderer, restrictions: restrictions?.Units })))));
};
export default RecipeParameterFormField;
